import React from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import AppShell from "../components/AppShell";
import { useContent } from "../hooks/useContent";
import { ChevronLeft } from "lucide-react";

export default function LibraryDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { LIBRARY } = useContent();
  const item = LIBRARY.find((x) => x.slug === slug);
  if (!item) {
    return (
      <AppShell>
        <div className="px-8 pt-16">
          <Link to="/library" className="text-brand">{t("library.back_to_library")}</Link>
          <p className="mt-4">{t("library.not_found")}</p>
        </div>
      </AppShell>
    );
  }
  return (
    <AppShell>
      <div className="px-8 pt-16 pb-10" data-testid={`library-detail-${slug}`}>
        <button onClick={() => navigate(-1)} className="flex items-center gap-1 text-sm text-foreground/55 hover:text-brand mb-2">
          <ChevronLeft className="w-4 h-4" /> {t("common.back")}
        </button>
        <p className="font-script text-2xl text-brand">{t("library.detail_kicker")}</p>
        <h1 className="font-serif text-4xl leading-tight">{item.title}</h1>
        <p className="mt-4 text-foreground/85 leading-relaxed text-lg">{item.short}</p>

        <section className="mt-8 rounded-3xl bg-card border border-border p-6">
          <p className="text-xs uppercase tracking-widest text-foreground/55">{t("library.feels")}</p>
          <p className="mt-2 font-serif italic text-foreground/85 leading-relaxed">{item.feels}</p>
        </section>

        <section className="mt-4 rounded-3xl bg-brand/8 border border-brand/20 p-6">
          <p className="text-xs uppercase tracking-widest text-brand-deep">{t("library.why_confusing")}</p>
          <p className="mt-2 text-foreground/85 leading-relaxed">{item.why_confusing}</p>
        </section>

        {Array.isArray(item.phases) && item.phases.length > 0 && (
          <section className="mt-4 rounded-3xl bg-card border border-border p-6 space-y-4" data-testid="library-phases">
            <p className="text-xs uppercase tracking-widest text-foreground/55">{t("library.phases")}</p>
            {item.phases.map((p, i) => (
              <div key={i} className="border-l-2 border-brand/30 pl-4">
                <div className="font-serif text-lg text-foreground">{i + 1}. {p.name}</div>
                <p className="text-sm text-foreground/80 mt-1 leading-relaxed">{p.body}</p>
              </div>
            ))}
          </section>
        )}

        {Array.isArray(item.tactics) && item.tactics.length > 0 && (
          <section className="mt-4 rounded-3xl bg-card border border-border p-6 space-y-4" data-testid="library-tactics">
            <p className="text-xs uppercase tracking-widest text-foreground/55">{t("library.tactics")}</p>
            {item.tactics.map((tk, i) => (
              <div key={i}>
                <div className="font-serif text-lg text-foreground">{tk.name}</div>
                <p className="text-sm text-foreground/80 mt-1 leading-relaxed">{tk.body}</p>
              </div>
            ))}
          </section>
        )}

        <section className="mt-6 rounded-3xl bg-card border border-border p-6">
          <p className="font-script text-xl text-brand">{t("library.gentle_kicker")}</p>
          <p className="font-serif italic text-foreground/85 mt-1">{t("library.gentle_body")}</p>
        </section>

        {/* Library → Decode loop */}
        <section className="mt-4 rounded-3xl bg-brand/8 border border-brand/20 p-6" data-testid="library-decode-cta">
          <p className="text-xs uppercase tracking-widest text-brand-deep">{t("library.reminds_me_kicker")}</p>
          <p className="mt-2 font-serif text-lg leading-snug">{t("library.reminds_me_title")}</p>
          <p className="mt-2 text-sm text-foreground/75 leading-relaxed">{t("library.reminds_me_body")}</p>
          <button
            data-testid="library-decode-btn"
            onClick={() => navigate("/decode?focus=1")}
            className="mt-4 inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-brand text-white text-sm hover:bg-brand/90 transition-all"
          >
            {t("library.reminds_me_cta")}
          </button>
        </section>
      </div>
    </AppShell>
  );
}
